import {
  type DirectoryIntegration,
  useIntegrations,
  useIntegrationTest,
  useUpdateIntegration,
} from "@features/integrations/api";
import { PageLayoutWithHelp } from "@features/page-help";
import { extractErrorMessage } from "@shared/extractErrorMessage";
import DrawerForm from "@shared/ui/drawer-form";
import {
  Alert,
  App as AntdApp,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Input,
  Result,
  Row,
  Skeleton,
  Space,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";

const typeMeta: Record<string, { title: string; description: string }> = {
  mail: {
    title: "Почта (Resend/SMTP)",
    description: "OTP, отчёты и уведомления по email.",
  },
  storage: {
    title: "Объектное хранилище",
    description: "S3-совместимое хранилище для файлов.",
  },
  telegram: {
    title: "Telegram",
    description: "Уведомления и отчёты через бота.",
  },
  ai: {
    title: "ИИ-анализ (Gemini)",
    description: "Автоматический анализ объектов учёта.",
  },
};

const webhookUrl = `${window.location.origin}/api/telegram/webhook`;

const metadataPlaceholders: Record<string, string> = {
  storage: `{
  "bucket": "example-bucket",
  "region": "ru-1",
  "public_endpoint": "https://s3.example.com",
  "internal_endpoint": "https://s3-int.example.com",
  "path_style": true
}`,
};

const formatUpdated = (value?: string) => {
  if (!value) return "—";
  return dayjs(value).format("DD.MM.YYYY HH:mm");
};

const AdminIntegrationsPage = () => {
  const integrationsQuery = useIntegrations();
  const integrations = integrationsQuery.data ?? [];
  const updateIntegration = useUpdateIntegration();
  const testIntegration = useIntegrationTest();
  const { message } = AntdApp.useApp();
  const [form] = Form.useForm();
  const [editing, setEditing] = useState<DirectoryIntegration | null>(null);

  useEffect(() => {
    if (!editing) return;
    const fields: Record<string, unknown> = {
      api_key: editing.api_key,
      from_email: editing.from_email,
      from_name: editing.from_name,
      reply_to: editing.reply_to,
      metadata: editing.metadata ?? "",
    };
    if (editing.type === "ai" && editing.metadata) {
      try {
        const meta = JSON.parse(editing.metadata);
        fields.ai_base_url = meta.base_url ?? "";
        fields.ai_model = meta.model ?? "";
      } catch {
        /* ignore */
      }
    }
    form.setFieldsValue(fields);
  }, [form, editing]);

  const handleSave = useCallback(
    async (values: Record<string, unknown>) => {
      if (!editing) return;
      try {
        let metadata = editing.type === "telegram" ? undefined : (values.metadata as string);
        if (editing.type === "telegram") {
          let existing: Record<string, unknown> = {};
          if (editing.metadata) {
            try {
              existing = JSON.parse(editing.metadata);
            } catch {
              /* ignore */
            }
          }
          existing.webhook_url = webhookUrl;
          metadata = JSON.stringify(existing);
        }
        if (editing.type === "ai") {
          metadata = JSON.stringify({
            base_url: (values.ai_base_url as string) || "https://proxlv.metrica.pro",
            model: (values.ai_model as string) || "gemini-2.5-flash",
          });
        }
        await updateIntegration.mutateAsync({
          slug: editing.slug,
          data: {
            slug: editing.slug,
            type: editing.type,
            api_key: values.api_key as string,
            from_email: values.from_email as string,
            from_name: values.from_name as string,
            reply_to: values.reply_to as string,
            metadata,
          },
        });
        message.success("Интеграция обновлена");
        setEditing(null);
      } catch (err) {
        message.error(extractErrorMessage(err));
      }
    },
    [editing, updateIntegration, message],
  );

  const handleTestMail = useCallback(async () => {
    if (!editing) return;
    const email = prompt("Введите email для тестового письма", "ne@metrica.pro");
    if (!email) return;
    try {
      await testIntegration.mutateAsync({ slug: editing.slug, data: { email } });
      message.success("Тестовое письмо отправлено");
    } catch (err) {
      message.error(extractErrorMessage(err));
    }
  }, [editing, testIntegration, message]);

  const handleTestTelegram = useCallback(async () => {
    if (!editing) return;
    try {
      const result = await testIntegration.mutateAsync({ slug: editing.slug, data: {} });
      if (result.ok) {
        message.success(`Бот @${result.bot_username} работает`);
      } else {
        message.error(result.error ?? "Ошибка подключения");
      }
    } catch (err) {
      message.error(extractErrorMessage(err));
    }
  }, [editing, testIntegration, message]);

  const isMail = editing?.type === "mail";
  const isStorage = editing?.type === "storage";
  const isTelegram = editing?.type === "telegram";
  const isAI = editing?.type === "ai";

  return (
    <PageLayoutWithHelp title="Интеграции">
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <Typography.Text type="secondary">
          Внешние сервисы: почта, хранилище, Telegram, ИИ-анализ. Нажмите «Настроить» для
          редактирования.
        </Typography.Text>

        {integrationsQuery.isLoading ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : integrations.length > 0 ? (
          <Row gutter={[16, 16]}>
            {integrations.map((integration) => {
              const meta = typeMeta[integration.type] ?? {
                title: integration.slug,
                description: "Интеграция",
              };
              const configured = Boolean(integration.api_key);
              return (
                <Col key={integration.slug} xs={24} sm={12} xl={6}>
                  <Card
                    hoverable
                    onClick={() => setEditing(integration)}
                    style={{ cursor: "pointer", height: "100%" }}
                  >
                    <Space orientation="vertical" size={4} style={{ width: "100%" }}>
                      <Badge
                        status={configured ? "success" : "default"}
                        text={configured ? "Настроена" : "Не настроена"}
                      />
                      <Typography.Text strong>{meta.title}</Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {meta.description}
                      </Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                        Обновлено: {formatUpdated(integration.updated_at ?? undefined)}
                      </Typography.Text>
                    </Space>
                  </Card>
                </Col>
              );
            })}
          </Row>
        ) : (
          <Result
            status="info"
            title="Интеграции не найдены"
            subTitle="Интеграции создаются автоматически при запуске системы."
          />
        )}
      </Space>

      <DrawerForm
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        onSubmit={handleSave}
        title={editing ? (typeMeta[editing.type]?.title ?? editing.slug) : ""}
        form={form}
        size="middle"
        loading={updateIntegration.isPending}
      >
        {isMail ? (
          <Alert
            type="info"
            showIcon
            description="Укажите Resend API-ключ и адреса отправителей."
            style={{ marginBottom: 16 }}
          />
        ) : null}
        {isTelegram ? (
          <Alert
            type="info"
            showIcon
            description="Укажите токен бота. Webhook регистрируется автоматически."
            style={{ marginBottom: 16 }}
          />
        ) : null}
        {isAI ? (
          <Alert
            type="info"
            showIcon
            description="Укажите API ключ Google AI. По умолчанию: gemini-2.5-flash."
            style={{ marginBottom: 16 }}
          />
        ) : null}

        <Form.Item
          label={
            isMail
              ? "Resend API key"
              : isTelegram
                ? "Токен бота"
                : isAI
                  ? "Google AI API Key"
                  : "API key / secret"
          }
          name="api_key"
          rules={[{ required: true, message: "Укажите API ключ" }]}
        >
          <Input.Password placeholder={isTelegram ? "123456:ABC-DEF..." : "re_..."} />
        </Form.Item>

        {isTelegram ? (
          <>
            <Form.Item label="Webhook URL">
              <Input value={webhookUrl} disabled />
            </Form.Item>
            <Button onClick={handleTestTelegram} loading={testIntegration.isPending}>
              Тестировать подключение
            </Button>
          </>
        ) : null}

        {isMail ? (
          <>
            <Form.Item
              label="Email отправителя"
              name="from_email"
              rules={[{ required: true, message: "Укажите email отправителя" }]}
            >
              <Input placeholder="noreply@acweb.local" />
            </Form.Item>
            <Form.Item
              label="Имя отправителя"
              name="from_name"
              rules={[{ required: true, message: "Укажите имя отправителя" }]}
            >
              <Input placeholder="AC Web" />
            </Form.Item>
            <Form.Item label="Reply-to" name="reply_to">
              <Input placeholder="support@acweb.local" />
            </Form.Item>
            <Form.Item label="Metadata (JSON)" name="metadata" extra="Дополнительные настройки.">
              <Input.TextArea rows={4} placeholder={"{\n  \n}"} />
            </Form.Item>
            <Button onClick={handleTestMail} loading={testIntegration.isPending}>
              Тестовое письмо
            </Button>
          </>
        ) : null}

        {isStorage ? (
          <Form.Item
            label="Metadata (JSON)"
            name="metadata"
            extra="JSON с параметрами: bucket, region, endpoint."
          >
            <Input.TextArea rows={8} placeholder={metadataPlaceholders.storage} />
          </Form.Item>
        ) : null}

        {isAI ? (
          <>
            <Form.Item label="Base URL (прокси)" name="ai_base_url">
              <Input placeholder="https://proxlv.metrica.pro" />
            </Form.Item>
            <Form.Item label="Модель" name="ai_model">
              <Input placeholder="gemini-2.5-flash" />
            </Form.Item>
          </>
        ) : null}
      </DrawerForm>
    </PageLayoutWithHelp>
  );
};

export default AdminIntegrationsPage;
